import { NgTemplateOutlet } from '@angular/common';
import {
	booleanAttribute,
	ChangeDetectionStrategy,
	Component,
	computed,
	ElementRef,
	inject,
	input,
	viewChild
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { map, of, scan, switchMap, timer } from 'rxjs';
import { IconComponent } from '../icon/icon.component';

type BaseTypeName = 'default' | 'primary' | 'accent' | 'success' | 'warning' | 'error' | 'info';
type CompositeTypeName = 'transparent' | 'frame';

export type ButtonTypeName = BaseTypeName | CompositeTypeName | `${CompositeTypeName}-${BaseTypeName}`;

const MIN_SPINNER_DURATION_MS = 1000;

@Component({
	selector: 'skl-button',
	template: `
		<ng-template #content>
			@if (showSpinnerRendered()) {
				<skl-icon
					name="spinner-ring-180-bg"
					class="spinner-icon"
					[class.is-leaving]="!loadingDelay()"
				/>
			}
			<span class="button-content-wrapper">
				<ng-content />
			</span>
		</ng-template>

		<ng-content select="[slot='group-start']" />

		@if (link()) {
			@if (isExternalLink()) {
				<a
					#focusableEl
					class="button"
					[attr.target]="target() ?? null"
					[attr.href]="isDisabledOrLoading() ? null : link()"
					[attr.aria-disabled]="isDisabledOrLoading() ? 'true' : null"
					[attr.tabindex]="isDisabledOrLoading() ? -1 : null"
				>
					<ng-container [ngTemplateOutlet]="content" />
				</a>
			} @else {
				<a
					#focusableEl
					class="button"
					[attr.target]="target() ?? null"
					[routerLink]="isDisabledOrLoading() ? null : link()"
					[queryParams]="queryParams()"
					[attr.aria-disabled]="isDisabledOrLoading() ? 'true' : null"
					[attr.tabindex]="isDisabledOrLoading() ? -1 : null"
				>
					<ng-container [ngTemplateOutlet]="content" />
				</a>
			}
		} @else {
			<button
				#focusableEl
				class="button"
				[attr.name]="name()"
				[disabled]="isDisabledOrLoading()"
			>
				<ng-container [ngTemplateOutlet]="content" />
			</button>
		}

		<ng-content select="[slot='group-end']" />
	`,
	styles: `
		:host {
			--surface: var(--surface-4-color);
			--_button-color: var(--skl-button-color, var(--_parent-button-color, var(--surface)));
			--_contrast: contrast-color(var(--_button-color));
			--_button-text-color: light-dark(
				color-mix(in oklch, var(--_button-color), var(--_contrast) 70%),
				color-mix(in oklch, var(--_button-color), var(--_contrast) 90%)
			);
			--_selected-background: var(--primary-color-light);
			--_button-padding-block: .5em;
			--_button-padding-inline: .75em;
			--_button-border-radius: var(--skl-border-radius, .25rem);
			--_button-font-size: var(--skl-button-font-size, .875rem);
			--_button-easing: cubic-bezier(0.4, 0, 0.2, 1);
			--_line-dark: oklch(from var(--_button-color) calc(l - .14) c h);
			--_line-light: oklch(from var(--_button-color) calc(l + .06) c h);
			--_button-shadow:
				0 0 0 1px var(--_line-dark),
				0 1px 2px 0 oklch(from var(--_button-color) calc(l - .3) c h / 42%),
				0 1px 0 0 var(--_line-light) inset;


			display: inline-flex;
			user-select: none;
			outline: none;

			interpolate-size: allow-keywords;
			transition:
				width 300ms var(--_button-easing),
				inline-size 300ms var(--_button-easing);
		}

		:host([disabled]) {
			pointer-events: none;

			&:not([data-no-disabled-state]) {
				opacity: .5;
			}

			.button {
				pointer-events: none;
			}
		}

		:host([data-type$="primary"]) {
			--_primary-color: light-dark(var(--primary-color-light), oklch(from var(--primary-color-light) calc(l - 0.3) c h));

			--_button-color: var(--_primary-color);
			--_button-color-for-children: var(--_primary-color);
		}

		:host([data-type$="accent"]) {
			--_complementary-color-base: oklch(75% 0.16 43.76);
			--_complementary-color: light-dark(var(--_complementary-color-base), oklch(from var(--_complementary-color-base) calc(l - 0.35) calc(c - 0.075) h));

			--_button-color: var(--_complementary-color);
			--_button-color-for-children: var(--_complementary-color);
		}

		:host([data-type$="success"]) {
			--_success-color: light-dark(var(--success-color), oklch(from var(--success-color) calc(l - 0.3) c h));

			--_button-color: var(--_success-color);
			--_button-color-for-children: var(--_success-color);
		}

		:host([data-type$="warning"]) {
			--_warning-color: light-dark(var(--warning-color), oklch(from var(--warning-color) calc(l - 0.1) c h));

			--_button-color: var(--_warning-color);
			--_button-color-for-children: var(--_warning-color);
		}

		:host([data-type$="error"]) {
			--_error-color: light-dark(var(--error-color), oklch(from var(--error-color) calc(l - 0.2) c h));

			--_button-color: var(--_error-color);
			--_button-color-for-children: var(--_error-color);
		}

		:host([data-type$="info"]) {
			--_info-color: light-dark(var(--info-color), oklch(from var(--info-color) calc(l - 0.2) c h));

			--_button-color: var(--_info-color);
			--_button-color-for-children: var(--_info-color);
		}

		:host([data-type|="transparent"]) {
			--_button-text-color: var(--_button-accent-color, var(--skl-button-text-color, light-dark(black, white)));

			.button {
				background-color: transparent;
				border-color: transparent;
				box-shadow: none;

				&:hover {
					background-color: color-mix(in oklab, transparent, light-dark(black, white) 8%);
					border-color: transparent;
					box-shadow: none;
				}

				&:active {
					background-color: color-mix(in oklab, transparent, light-dark(black, white) 22%);
					border-color: transparent;
					box-shadow: none;
				}
			}
		}

		:host([data-type|="frame"]) {
			.button {
				background-color: transparent;
				box-shadow: inset 0px 0px 0px 1px var(--_button-color);

				&:hover {
					background-color: color-mix(in oklab, transparent, light-dark(black, white) 8%);
					border-color: transparent;
					box-shadow: none;
				}

				&:active {
					background-color: color-mix(in oklab, transparent, light-dark(black, white) 22%);
					border-color: transparent;
					box-shadow: none;
				}
			}
		}

		:host([data-size="small"]) > .button {
			--_button-padding-block: .25em;
			--_button-padding-inline: .5em;
		}

		:host([data-size="tiny"]) > .button {
			--_button-padding-block: .125em;
			--_button-padding-inline: .25em;
		}

		:host([data-hide-content][data-loading]) {
			.button {
				position: relative;
			}

			.button-content-wrapper {
				opacity: 0;
				transform: scale(0.85);
				visibility: hidden;
			}

			.spinner-icon {
				position: absolute;
				top: 50%;
				left: 50%;
				transform: translate(-50%, -50%);
				margin: 0;

				&.is-leaving {
					opacity: 0;
					transform: translate(-50%, -50%) scale(0.5);
				}

				@starting-style {
					opacity: 0;
					transform: translate(-50%, -50%) scale(0.5);
				}
			}
		}

		.button-content-wrapper {
			display: inline-flex;
			align-items: center;
			justify-content: center;
			gap: inherit;
			opacity: 1;
			transform: scale(1);
			transition:
				opacity 300ms var(--_button-easing),
				transform 300ms var(--_button-easing),
				visibility 300ms allow-discrete;
			transition-behavior: allow-discrete;
		}

		:host(.icon-only) > .button,
		:host([data-kind="icon-only"]) > .button {
			--_button-padding-inline: var(--_button-padding-block);

			aspect-ratio: 1;
		}

		:host:has([slot="group-start"]) {
			> .button {
				border-top-left-radius: 0;
				border-bottom-left-radius: 0;
			}
		}

		:host:has([slot="group-end"]) {
			> .button {
				border-top-right-radius: 0;
				border-bottom-right-radius: 0;
			}
		}

		:host ::ng-deep [slot="group-start"] {
			--_parent-button-color: var(--_button-color-for-children);
			--_parent-button-text-color: var(--_button-text-color-for-children);

			&,
			.button {
				border-top-right-radius: 0;
				border-bottom-right-radius: 0;
				border-right: 1px solid var(--_line-dark);
			}
		}

		:host ::ng-deep [slot="group-end"] {
			--_parent-button-color: var(--_button-color-for-children);
			--_parent-button-text-color: var(--_button-text-color-for-children);

			&,
			.button {
				border-top-left-radius: 0;
				border-bottom-left-radius: 0;
				border-left: 1px solid var(--_line-light);
			}
		}

		.button {
			all: unset;
			cursor: pointer;
			touch-action: manipulation;
			position: relative;
			display: inline-flex;
			justify-content: center;
			align-items: center;
			text-align: center;
			gap: .5em;
			flex: 1;
			min-height: 2.25rem;
			box-sizing: border-box;
			color: var(--_button-text-color);
			background-color: var(--_button-color);
			padding-inline: var(--_button-padding-inline);
			padding-block: var(--_button-padding-block);
			border-radius: var(--_button-border-radius);
			font-size: var(--_button-font-size);
			font-weight: 500;
			user-select: none;
			-webkit-tap-highlight-color: transparent;
			-webkit-touch-callout: none;

			transition:
				width 300ms var(--_button-easing),
				inline-size 300ms var(--_button-easing),
				background-color 300ms ease,
				box-shadow 145ms ease,
				outline-offset 145ms ease;

			box-shadow: var(--_button-shadow);

			&:hover {
				background-color: color-mix(in oklab, var(--_button-color), light-dark(black, white) 8%);

				box-shadow:
					0 0 0 1px oklch(from var(--_button-color) calc(l - .1) c h),
					0 1px 2px 0 oklch(from var(--_button-color) calc(l - .3) c h / 42%);
			}

			&:active {
				background-color: color-mix(in oklab, var(--_button-color), light-dark(black, white) 22%);
				box-shadow: none;
			}
		}

		.button:focus-visible {
			outline-offset: .1875rem;
			outline: 2px solid var(--focus-outline, var(--_selected-background));
			z-index: 1;
		}

		.spinner-icon {
			display: inline-flex;
			opacity: 1;
			transform: scale(1);
			width: 1em;
			max-width: 1.5em;
			margin-right: 0;
			overflow: hidden;
			transition:
				opacity 300ms var(--_button-easing),
				transform 300ms var(--_button-easing),
				width 300ms var(--_button-easing),
				max-width 300ms var(--_button-easing),
				margin-right 300ms var(--_button-easing),
				display 300ms allow-discrete;
			transition-behavior: allow-discrete;

			&.is-leaving {
				opacity: 0;
				transform: scale(0.5);
				width: 0;
				max-width: 0;
				margin-right: -0.5em;
			}

			@starting-style {
				opacity: 0;
				transform: scale(0.5);
				width: 0;
				max-width: 0;
				margin-right: -0.5em;
			}
		}
	`,
	host: {
		'[attr.disabled]': "isDisabledOrLoading() ? '' : null",
		'[attr.data-no-disabled-state]': "loadingDelay() && !disabled() ? '' : null",
		'[attr.data-type]': "typeName() ?? 'default'",
		'[attr.data-hide-content]': "hideContentOnSpinner() ? '' : null",
		'[attr.data-loading]': "showSpinnerRendered() ? '' : null",
		'(click)': 'onClick($event)',
	},
	imports: [NgTemplateOutlet, RouterLink, IconComponent],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ButtonComponent {
	readonly name = input<string | undefined>(undefined);
	readonly link = input<string | undefined>(undefined);
	readonly queryParams = input<any | undefined>(undefined);
	readonly target = input<string | undefined>(undefined);
	readonly disabled = input<boolean, unknown>(false, { transform: booleanAttribute });
	readonly typeName = input<ButtonTypeName | undefined>('default');
	readonly spinner = input<boolean, unknown>(false, { transform: booleanAttribute });
	readonly hideContentOnSpinner = input<boolean, unknown>(true, { transform: booleanAttribute });

	readonly loadingDelay = toSignal(
		toObservable(this.spinner).pipe(
			scan(
				(acc, isSpinnerOn) => ({
					isSpinnerOn,
					startTime: isSpinnerOn ? Date.now() : acc.startTime,
				}),
				{ isSpinnerOn: false, startTime: 0 }
			),
			switchMap(({ isSpinnerOn, startTime }) => {
				if (isSpinnerOn) {
					return of(true);
				}
				if (!startTime) {
					return of(false);
				}
				const remainingMs = Math.max(0, MIN_SPINNER_DURATION_MS - (Date.now() - startTime));
				return remainingMs > 0 ? timer(remainingMs).pipe(map(() => false)) : of(false);
			})
		),
		{ initialValue: false }
	);

	readonly showSpinnerRendered = toSignal(
		toObservable(this.loadingDelay).pipe(
			switchMap(isLoading => {
				if (isLoading) {
					return of(true);
				}
				return timer(300).pipe(map(() => false));
			})
		),
		{ initialValue: false }
	);

	private readonly elementRef = inject(ElementRef);
	protected readonly focusableEl = viewChild<ElementRef<HTMLButtonElement | HTMLAnchorElement>>('focusableEl');

	protected readonly isDisabledOrLoading = computed(() => this.disabled() || this.loadingDelay());

	protected readonly isExternalLink = computed(() => {
		const linkVal = this.link();
		if (!linkVal) return false;
		return Boolean(this.target()) || linkVal.startsWith('http://') || linkVal.startsWith('https://') || linkVal.startsWith('//');
	});

	protected onClick(event: MouseEvent) {
		if (this.isDisabledOrLoading()) {
			event.stopImmediatePropagation();
			event.preventDefault();
			return;
		}

		if (!this.link() && !this.elementRef.nativeElement.closest('a')) {
			event.stopPropagation();
		}
	}

	protected focus() {
		this.focusableEl()?.nativeElement?.focus();
	}
}
